export type AttachmentKind = 'image' | 'video' | 'audio' | 'file';

// No server-side transcoding in v1 (see HandlesThreadAttachments::attachmentMaxSizes()) --
// reject oversized files client-side before spending the upload. Keep these in sync with that
// method -- this is only the early client-side check; the server enforces it for real.
export const ATTACHMENT_MAX_SIZES: Record<AttachmentKind, number> = {
  image: 5 * 1024 * 1024,
  audio: 10 * 1024 * 1024,
  video: 50 * 1024 * 1024,
  file: 25 * 1024 * 1024,
};

// Extensions with no legitimate use in a coaching chat -- blocked regardless of size, matching
// HandlesThreadAttachments::blockedAttachmentExtensions() on the server (which is what actually
// enforces this; this is just an early check to save the upload).
const BLOCKED_EXTENSIONS = [
  'exe', 'msi', 'bat', 'cmd', 'com', 'scr', 'js', 'jar', 'apk', 'app', 'dmg', 'pkg',
  'sh', 'ps1', 'dll', 'vbs', 'wsf', 'jse', 'cpl', 'gadget', 'hta', 'lnk', 'reg',
];

export function extensionOf(filename: string): string {
  const dot = filename.lastIndexOf('.');
  return dot === -1 ? '' : filename.slice(dot + 1).toLowerCase();
}

// FontAwesome icon (solid-family) for a generic 'file' attachment's card, chosen by extension --
// shared by AttachmentGrid (sent/received) and ThreadComposer (staged, pre-send). Anything not
// covered here (including .fit) falls back to a plain file icon rather than guessing.
const FILE_ICONS: Record<string, string> = {
  pdf: 'fa-file-pdf',
  doc: 'fa-file-word',
  docx: 'fa-file-word',
  xls: 'fa-file-excel',
  xlsx: 'fa-file-excel',
  csv: 'fa-file-csv',
  ppt: 'fa-file-powerpoint',
  pptx: 'fa-file-powerpoint',
  zip: 'fa-file-zipper',
  rar: 'fa-file-zipper',
  '7z': 'fa-file-zipper',
  txt: 'fa-file-lines',
};

export function fileIconClass(name: string | undefined | null): string {
  return FILE_ICONS[extensionOf(name ?? '')] ?? 'fa-file';
}

// Never null -- anything that isn't image/video/audio (a .fit file, a PDF, a spreadsheet, ...)
// falls back to the generic 'file' kind rather than being rejected. Mirrors
// HandlesThreadAttachments::attachmentTypeFor() on the server.
export function attachmentKindFor(mime: string): AttachmentKind {
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('video/')) return 'video';
  if (mime.startsWith('audio/')) return 'audio';
  return 'file';
}

export function validateAttachment(file: File): { kind: AttachmentKind } | { error: string } {
  if (BLOCKED_EXTENSIONS.includes(extensionOf(file.name))) return { error: "That file type isn't allowed." };
  const kind = attachmentKindFor(file.type);
  if (file.size > ATTACHMENT_MAX_SIZES[kind]) return { error: 'Attachment is too large.' };
  return { kind };
}

export function attachmentPreviewLabel(kind: string): string {
  return `[${kind}]`;
}

// Shared "what shows in the sidebar/preview" text for a message with a body and/or N attachments
// -- body wins if present, else a single attachment's type, else a count for several.
export function messagePreviewText(message: { body: string | null; attachments: { type: string }[] }): string {
  if (message.body) return message.body;
  if (message.attachments.length === 0) return '';
  if (message.attachments.length === 1) return attachmentPreviewLabel(message.attachments[0].type);
  return `[${message.attachments.length} attachments]`;
}

// Human-readable file size, e.g. "1.2 MB" -- used by the generic file attachment card.
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let value = bytes / 1024;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex++;
  }
  return `${value.toFixed(value < 10 ? 1 : 0)} ${units[unitIndex]}`;
}
